import api from '../apiUtils'

// ============================================================================
// Service "Invitations campagne" (push groupe marque -> createurs)
// Branche les endpoints backend wardogz-ugc :
//   POST /brands/:id/campaign-invites
//   GET  /companies/:id/message-credits
//   POST /companies/:id/message-credits/purchase
// ============================================================================

export interface MessagePack {
  id: string
  name: string
  description?: string
  nb_credits: number
  price: number
  popular?: boolean
}

export interface MessageCreditsResponse {
  balance: number
  packs: MessagePack[]
  history: Array<{
    _id: string
    amount: number
    createdAt: string
  }>
}

export interface SendInvitesResponse {
  batchId?: string
  sent: number
  skipped: Array<{ userId: string; reason: string }>
  remainingCredits: number
  idempotent?: boolean
}

export const getMessageCredits = async (
  companyId: string
): Promise<MessageCreditsResponse> => {
  const { data } = await api.get<MessageCreditsResponse>(
    `/companies/${companyId}/message-credits`
  )
  return data
}

export const purchaseMessageCredits = async (
  companyId: string,
  packId: string,
  redirect?: string
): Promise<{ url: string }> => {
  const { data } = await api.post<{ url: string }>(
    `/companies/${companyId}/message-credits/purchase`,
    { packId, redirect }
  )
  return data
}

export const sendCampaignInvites = async (
  brandId: string,
  payload: {
    briefId: string
    userIds: string[]
    template: string
    batchId?: string
  }
): Promise<SendInvitesResponse> => {
  const { data } = await api.post<SendInvitesResponse>(
    `/brands/${brandId}/campaign-invites`,
    payload
  )
  return data
}

// Statut de candidature (BriefApplication) : source de verite de la conversion.
// L'invitation ne porte QUE son cycle propre (sent/opened).
export type ApplicationStatus =
  | 'created'
  | 'enrolled'
  | 'applied'
  | 'step2'
  | 'won'
  | 'lost'
  | 'declined'
  | 'declined-client'
  | null

export interface CampaignInvite {
  _id: string
  user: string
  brief: { _id: string; name?: string } | null
  inviteStatus: 'sent' | 'opened'
  applicationStatus: ApplicationStatus
  createdAt: string
}

export const getCampaignInvites = async (
  brandId: string
): Promise<CampaignInvite[]> => {
  const { data } = await api.get<{ invites: CampaignInvite[] }>(
    `/brands/${brandId}/campaign-invites`
  )
  return data.invites ?? []
}

// ----------------------------------------------------------------------------
// Helpers funnel (cote front) : on derive les etapes a partir des 2 cycles.
//   Invite  : toujours vrai (il y a une invitation)
//   Vu      : inviteStatus === 'opened' OU candidature existante (a forcement vu)
//   Inscrit : applicationStatus parmi enrolled/applied/step2/won/lost (interet enregistre)
//   Postule : applicationStatus parmi applied/step2/won/lost
//   Gagne   : applicationStatus === 'won'
// ----------------------------------------------------------------------------
export const APPLIED_STATUSES: ApplicationStatus[] = ['applied', 'step2', 'won', 'lost']
export const ENROLLED_STATUSES: ApplicationStatus[] = ['enrolled', 'applied', 'step2', 'won', 'lost']

export interface FunnelStats {
  invited: number
  opened: number
  enrolled: number
  applied: number
  won: number
}

export const isApplied = (s: ApplicationStatus): boolean =>
  !!s && APPLIED_STATUSES.includes(s)

export const isEnrolled = (s: ApplicationStatus): boolean =>
  !!s && ENROLLED_STATUSES.includes(s)

export const computeFunnel = (invites: CampaignInvite[]): FunnelStats => {
  return invites.reduce<FunnelStats>(
    (acc, i) => {
      acc.invited += 1
      if (i.inviteStatus === 'opened' || i.applicationStatus) acc.opened += 1
      if (isEnrolled(i.applicationStatus)) acc.enrolled += 1
      if (isApplied(i.applicationStatus)) acc.applied += 1
      if (i.applicationStatus === 'won') acc.won += 1
      return acc
    },
    { invited: 0, opened: 0, enrolled: 0, applied: 0, won: 0 }
  )
}
